import {
    SOV_CONTEXTUAL_NODE_KEYS, TYPE_OF_PURCHASE
} from '../../constants/DetailedBookingConstants';
import { FORM_FIELDS } from '../../constants/FormFieldsConstants';
import {
    DetailedBookingSOVData, PreviousSOVValues, SlotOptionType, SlotPackage, SovActiveModalType, SOVContextualNodeData
} from '../../interface/DetailedBookingInterface';
import { getProcessedValue } from '../helpers';
import { checkContextualPercentageValidation, checkContextualValueValidation } from './helpers';
import { reCalculateValueBlockedByLineItem } from './reCalculateValueBlockedByLineItem';

export interface HandleSOVContextualNodeChangeProps {
    nodeId: string;
    field: string;
    value: string;
    setContextualNodes: React.Dispatch<React.SetStateAction<SOVContextualNodeData[]>>;
    formData: DetailedBookingSOVData;
    isContextualSelected: boolean;
    slotPackagesData: SlotPackage[];
    setFormData: React.Dispatch<React.SetStateAction<DetailedBookingSOVData>>;
    setActiveModal: SovActiveModalType;
    roValue: number;
    contextualNodes: SOVContextualNodeData[];
    setPreviousValues: React.Dispatch<React.SetStateAction<PreviousSOVValues | null>>;
    isEditMode: boolean;
    selectedOption?: SlotOptionType;
    setContextualPercentageAvailable?: React.Dispatch<React.SetStateAction<number>>;
    setCurrentValidationNodeId?: React.Dispatch<React.SetStateAction<string | null>>;
    isApprovedBooking?: boolean;
    existingContextualNodePercentages?: Map<string, number>;
    isWaitlistEnabled?: boolean;
}

export const handleSOVContextualNodeChange = ({
    nodeId,
    field,
    value,
    setContextualNodes,
    formData,
    isContextualSelected,
    slotPackagesData,
    setFormData,
    setActiveModal,
    roValue,
    contextualNodes,
    setPreviousValues,
    isEditMode,
    selectedOption,
    setContextualPercentageAvailable,
    setCurrentValidationNodeId,
    isApprovedBooking,
    existingContextualNodePercentages,
    isWaitlistEnabled,
}: HandleSOVContextualNodeChangeProps) => {
    const numericFields: string[] = [
        FORM_FIELDS.SOV_PERCENTAGE_FOR_CONTEXTUAL_GP,
        FORM_FIELDS.IMPRESSION_BOOKED,
        FORM_FIELDS.VALUE_BOOKED,
    ];
    const processedValue = getProcessedValue(numericFields, field, value);
    const { sovPercentageForGP, valueBlockedByLineItem, gpSlot, typeOfPurchase } = formData;

    if (field === FORM_FIELDS.SOV_PERCENTAGE_FOR_CONTEXTUAL_GP) {
        const sovPercentage = processedValue as number;
        const currentNode = contextualNodes.find(node => node.id === nodeId);
        const contextualNodeName = currentNode?.contextualNode || '';

        // Store previous values before validation in edit mode
        if (isEditMode && currentNode) {
            const { sovPercentageForContextualGP, valueBooked, impressionBooked } = currentNode;
            setPreviousValues({
                sovPercentageForGP,
                valueBlockedByLineItem,
                contextualNode: {
                    sovPercentageForContextualGP,
                    impressionBooked,
                    valueBooked,
                },
            });
        }

        const existingNodePercentage = isApprovedBooking && existingContextualNodePercentages
            ? existingContextualNodePercentages.get(contextualNodeName)
            : undefined;

        if (checkContextualPercentageValidation(
            sovPercentage,
            setActiveModal,
            contextualNodeName,
            gpSlot,
            slotPackagesData,
            selectedOption,
            setContextualPercentageAvailable,
            isApprovedBooking,
            existingNodePercentage,
            isWaitlistEnabled,
        )) {
            setCurrentValidationNodeId?.(nodeId);
            return;
        }


        // For contextual GP SOV selections, calculate impressionBooked and valueBooked
        // when sovPercentageForContextualGP changes
        if (typeOfPurchase === TYPE_OF_PURCHASE.GP_SOV && isContextualSelected) {
            setContextualNodes((prev) => {
                const updatedNodes = prev.map((node) => {
                    if (node.id === nodeId) {
                        const updatedNode = { ...node, [field]: processedValue as number };

                        const selectedPackageData = slotPackagesData.find(
                            (pkg) => pkg.slotPackageName === formData.gpSlot
                        );
                        const selectedSegment = selectedPackageData?.segments.find(
                            ({ segmentName }) => segmentName === node.contextualNode
                        );

                        if (selectedSegment && node.contextualNode) {
                            const calculatedImpressionBooked = Math.floor((selectedSegment.overAllImpressions * sovPercentage) / 100);
                            const calculatedValueBooked = Math.floor(
                                (calculatedImpressionBooked * selectedSegment.cpm) / 1000
                            );

                            if (!isEditMode && checkContextualValueValidation(calculatedValueBooked, roValue, setActiveModal)) {
                                setCurrentValidationNodeId?.(nodeId);
                                return node;
                            }

                            updatedNode.impressionBooked = calculatedImpressionBooked;
                            updatedNode.valueBooked = calculatedValueBooked;
                        }

                        return updatedNode;
                    }
                    return node;
                });

                if (
                    typeOfPurchase === TYPE_OF_PURCHASE.GP_SOV &&
                    isContextualSelected
                ) {
                    const totalValueBlocked = updatedNodes.reduce(
                        (sum, node) => sum + (node.valueBooked || 0),
                        0
                    );

                    setFormData((prev) => ({
                        ...prev,
                        valueBlockedByLineItem: totalValueBlocked,
                    }));
                }

                return updatedNodes;
            });
        } else {
            // For non-GP SOV cases, just update the field
            setContextualNodes((prev) => prev.map((node) => {
                if (node.id === nodeId) {
                    return { ...node, [field]: processedValue as number };
                }
                return node;
            }));
        }
        return;
    }
    if (field === FORM_FIELDS.VALUE_BOOKED) {
        const transformedProcessedValue = processedValue as number;
        const currentNode = contextualNodes.find(node => node.id === nodeId);

        if (!isEditMode && checkContextualValueValidation(transformedProcessedValue, roValue, setActiveModal)) {
            // Store previous values before validation in edit mode
            if (isEditMode && currentNode) {
                const { sovPercentageForContextualGP, impressionBooked, valueBooked } = currentNode;
                setPreviousValues({
                    sovPercentageForGP,
                    valueBlockedByLineItem,
                    contextualNode: {
                        sovPercentageForContextualGP,
                        impressionBooked,
                        valueBooked,
                    },
                });
            }
            setCurrentValidationNodeId?.(nodeId);
            return;
        }
    }

    setContextualNodes((prev) => {
        const updatedNodes = prev.map((node) => {
            if (node.id === nodeId) {
                if (field === SOV_CONTEXTUAL_NODE_KEYS.CONTEXTUAL_NODE) {
                    if (node.contextualNode !== processedValue as string) return {
                        ...node,
                        [field]: processedValue as string,
                        sovPercentageForContextualGP: 0,
                        impressionBooked: 0,
                        valueBooked: 0
                    };
                    return { ...node, [field]: processedValue as string };
                } else if (numericFields.includes(field)) return { ...node, [field]: processedValue };
                else return { ...node, [field]: processedValue };
            }
            return node;
        });

        if (
            (field === SOV_CONTEXTUAL_NODE_KEYS.CONTEXTUAL_NODE || field === FORM_FIELDS.VALUE_BOOKED) &&
            formData.typeOfPurchase === TYPE_OF_PURCHASE.GP_SOV &&
            isContextualSelected
        ) reCalculateValueBlockedByLineItem({
            nodes: updatedNodes,
            typeOfPurchase: formData.typeOfPurchase,
            isContextualSelected,
            setFormData,
        });

        return updatedNodes;
    });
};
